import { initSearchInput } from "../form-input-search/form-input-search.js";
import { initListBox } from "../listbox/listbox.js";
const handleDropdownInputListeners = ({
  clearInput,
  container,
  input,
  listbox,
  onClick,
  onKeyDown,
  searchInput,
  setExpanded,
  toggleExpanded,
  tooltip
}) => {
  if (!input || !listbox || !tooltip) {
    return () => null;
  }
  const clearBtn = input.parentElement?.querySelector(
    ".tds-form-input-leading .tds-chip .tds-icon-btn"
  );
  if (!container) {
    container = input.closest(".tds-form-input");
  }
  const handleInputKeyDown = (e) => {
    switch (e.key) {
      case " ":
      case "ArrowDown":
      case "ArrowUp":
      case "Enter":
      case "Return":
        e.preventDefault();
        setExpanded(true);
        break;
    }
    handleKeyDown(e);
  };
  const handleKeyDown = (e) => {
    switch (e.key) {
      case "Backspace":
        if (!searchInput?.value && document.activeElement?.className.match("tds-form-input-dropdown")) {
          clearInput();
        }
        break;
      case "Escape":
        if (!searchInput?.value) {
          setExpanded(false);
        }
        break;
      case "Tab":
        setExpanded(false, { autoFocus: false });
        break;
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };
  input.addEventListener("keydown", handleInputKeyDown);
  searchInput?.addEventListener("keydown", handleKeyDown);
  const handleContainerClick = (e) => {
    if (!e.target.closest("button, .tds-tooltip")) {
      toggleExpanded();
    }
    if (onClick) {
      onClick(e);
    }
  };
  container?.addEventListener("click", handleContainerClick);
  const handleClearClick = () => {
    clearInput();
  };
  clearBtn?.addEventListener("click", handleClearClick);
  const handleWindowClick = (e) => {
    if (!container?.contains(e.target)) {
      setExpanded(false);
      const target = e.target;
      if (!(target.tagName === "LABEL" && target.getAttribute("for") == input.getAttribute("id")) && searchInput) {
        setTimeout(() => {
          searchInput.value = "";
          searchInput.dispatchEvent(new Event("input"));
        }, 500);
      }
    }
  };
  if (tooltip.classList.contains("tds-tooltip--open")) {
    window.addEventListener("click", handleWindowClick);
  }
  const observer = new MutationObserver((mutationList) => {
    for (const mutation of mutationList) {
      if (mutation.attributeName === "class" && tooltip.classList.contains("tds-tooltip--open")) {
        window.addEventListener("click", handleWindowClick);
      } else {
        window.removeEventListener("click", handleWindowClick);
      }
    }
  });
  observer.observe(tooltip, { attributes: true });
  return () => {
    input.removeEventListener("keydown", handleInputKeyDown);
    searchInput?.removeEventListener("keydown", handleKeyDown);
    container?.removeEventListener("click", handleContainerClick);
    clearBtn?.removeEventListener("click", handleClearClick);
    window.removeEventListener("click", handleWindowClick);
    observer.disconnect();
  };
};
const initDropdownInput = (component) => {
  const input = component.querySelector(".tds-form-input-dropdown");
  const searchInput = component.querySelector(".tds-form-input-search");
  const listbox = component.querySelector(".tds-listbox");
  const tooltip = component.querySelector(".tds-tooltip");
  const leadingChip = component.querySelector(".tds-form-input-leading .tds-chip");
  const trailingIcon = component.querySelector(".tds-form-input-trailing .tds-icon");
  if (!input || !listbox || !tooltip || !leadingChip) {
    console.warn(
      "TDS Dropdown Input malformed, requires at minimum, an input, a tooltip, and a listbox"
    );
    return;
  }
  const { multiple } = input;
  const setExpanded = (expand, options = {}) => {
    const { autoFocus = true } = options;
    if (expand) {
      tooltip.classList.remove("tds-tooltip--closed");
      tooltip.classList.add("tds-tooltip--open");
      trailingIcon?.classList.add("tds-icon--flip-x");
      if (autoFocus)
        searchInput?.focus();
    } else {
      tooltip.classList.remove("tds-tooltip--open");
      tooltip.classList.add("tds-tooltip--closed");
      trailingIcon?.classList.remove("tds-icon--flip-x");
      if (autoFocus)
        input?.focus();
    }
  };
  const toggleExpanded = (options = {}) => {
    if (tooltip.classList.contains("tds-tooltip--open")) {
      setExpanded(false, options);
      if (searchInput) {
        setTimeout(() => {
          searchInput.value = "";
          searchInput.dispatchEvent(new Event("input"));
        }, 500);
      }
    } else {
      setExpanded(true, options);
    }
  };
  const clearInput = () => {
    input.value = "";
    renderLeading();
    if (searchInput) {
      setTimeout(() => {
        searchInput.value = "";
        searchInput.dispatchEvent(new Event("input"));
      }, 500);
    }
  };
  const onOptionSelect = ({ label, value }) => {
    if (multiple || Array.isArray(value) || Array.isArray(label)) {
      const prevSelected = input.value !== "" ? input.value.split(",") : [];
      input.value = prevSelected.find((name) => name === label) ? prevSelected.filter((name) => name !== label).toString() : [...prevSelected, label].toString();
    } else {
      input.value = (label === input.value ? "" : label) || "";
      setExpanded(false);
    }
    renderLeading();
    if (searchInput) {
      setTimeout(() => {
        searchInput.value = "";
        searchInput.dispatchEvent(new Event("input"));
      }, 500);
    }
  };
  const renderLeading = () => {
    const close = leadingChip.querySelector(".tds-icon-btn");
    let text = leadingChip.querySelector(".tds-chip-text");
    if (!text) {
      text = document.createElement("span");
      text.classList.add("tds-chip-text");
      leadingChip.prepend(text);
    }
    if (input.value === "") {
      text.innerText = input.placeholder;
      leadingChip.classList.add("tds-placeholder");
      close?.style.setProperty("display", "none");
    } else {
      const selected = input.value.split(",");
      if (selected.length === 1) {
        text.innerText = input.value;
        leadingChip.classList.remove("tds-placeholder");
        close?.style.setProperty("display", "none");
      }
      if (selected.length > 1) {
        text.innerText = `${selected.length} Selected`;
        leadingChip.classList.remove("tds-placeholder");
        close?.style.removeProperty("display");
      }
    }
  };
  const cleanup = handleDropdownInputListeners({
    clearInput,
    container: component,
    input,
    listbox,
    searchInput,
    setExpanded,
    toggleExpanded,
    tooltip
  });
  const searchCleanup = initSearchInput(component);
  const listBoxCleanup = initListBox(listbox, {
    container: component,
    input,
    multiple,
    onOptionSelect,
    searchInput
  });
  return () => {
    cleanup();
    searchCleanup();
    listBoxCleanup();
  };
};
const initDropdownInputs = (props = {}) => {
  const { parent = document } = props;
  const inputs = Array.from(parent.querySelectorAll(".tds-form-input-dropdown"));
  inputs.forEach((input) => {
    const component = input.closest(".tds-form-input");
    if (component) {
      initDropdownInput(component);
    }
  });
};
export {
  handleDropdownInputListeners,
  initDropdownInput,
  initDropdownInputs
};
